import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import {ButtonComponent, CheckboxComponent, IconComponent, TileCasePipe} from '@kin/spark-ui';
import type { StripeCheckoutElementsSdk } from '@stripe/stripe-js';
import { UserProfileService } from '../user-profile.service';
import { PaymentService } from '../payments/payment.service';
import { PAYMENT_CONFIG } from '../payments/payment-config';
import type { PartnerLead } from '@kin/property-tax-types';

const PAYMENT_ERROR = 'We could not process your payment. Please check your details and try again.';

const PAYMENT_PENDING = 'Your payment is still processing. We will email you as soon as it completes.';

/** Florida's statutory VAB filing-fee cap, in dollars. */
const STATUTORY_FILING_FEE_CAP = 50;

@Component({
  selector: 'app-partner-redirect',
  standalone: true,
  imports: [ButtonComponent, CheckboxComponent, IconComponent, CurrencyPipe, TileCasePipe],
  templateUrl: './partner-redirect.component.html',
  styleUrl: './partner-redirect.component.css',
})
export class PartnerRedirectComponent implements OnInit {
  private userProfile = inject(UserProfileService);
  private payments = inject(PaymentService);
  private paymentConfig = inject(PAYMENT_CONFIG);
  private cdr = inject(ChangeDetectorRef);

  agreedTerms = false;
  agreedSharing = false;
  showInterstitial = false;
  saving = false;
  saveError = '';

  /** Payment step state. All of it stays inert while `acceptPayments` is false. */
  showPayment = false;
  preparingPayment = false;
  paying = false;
  paymentError = '';
  paymentPending = '';
  resolvingReturn = false;

  /**
   * Set when the user comes back from an off-site authentication (3DS or a redirect-based
   * method). Snapshotted at bootstrap rather than read here: the router has already rewritten
   * the URL and dropped the query by the time `ngOnInit` runs. Resolved against the server —
   * the client's own `confirm()` result is never what decides "paid".
   */
  returnedSessionId: string | null = null;

  private checkout: StripeCheckoutElementsSdk | null = null;

  @ViewChild('paymentElement') private paymentElementRef?: ElementRef<HTMLElement>;

  get acceptPayments(): boolean {
    return this.paymentConfig.acceptPayments;
  }

  /**
   * Florida's statutory VAB filing-fee cap. Kept only for the two places that are not the
   * breakdown: the contingency copy shown with payments off, and the no-lead page.
   */
  private readonly statutoryFilingFeeCap = STATUTORY_FILING_FEE_CAP;

  /** The figure in today's contingency copy, which the flag-off branch must keep verbatim. */
  contingencyFilingFee = STATUTORY_FILING_FEE_CAP;

  /**
   * What this lead's county actually charges to accept a petition, in dollars. Florida's real
   * fees run $15 to $50, so a flat $50 overstated every county.
   *
   * Taken straight off the lead: the server resolves an unknown, blank or unmatched county to
   * the statutory cap before it ever reaches us, so there is deliberately **no** client-side
   * fallback for a missing figure — one place defines that rule and it is not here. The cap
   * below covers only the case of no lead at all, which is the pre-existing no-lead path.
   */
  get filingFee(): number {
    const lead = this.partnerLead;
    return lead ? lead.countyFilingFeeCents / 100 : this.statutoryFilingFeeCap;
  }

  /** The whole upfront charge. Must match the server's own constant — the client never sends it. */
  totalFee = 70;

  /**
   * What is left after the county takes its cut. Derived, never a fourth constant: the breakdown
   * renders total, fee and balance side by side, and a hard-coded $20 would silently stop
   * reconciling the moment either constant moved.
   */
  get serviceFee(): number {
    return this.totalFee - this.filingFee;
  }

  /** Where the receipt goes. Null when the profile has not loaded an owner; the copy degrades. */
  get receiptEmail(): string | null {
    return this.userProfile.profile?.owner?.email ?? null;
  }

  /**
   * True when this user's most recent payment is `paid`. The timestamp rides on the profile
   * that `nonFloridaGuard` already loaded, so knowing this costs no extra request — and unlike
   * `GET /payments/status` it needs no `session_id`, which is exactly why a plain refresh used
   * to show a paid user an empty payment form.
   *
   * A refund clears it server-side, so a refunded user correctly sees the payment step again.
   *
   * Gated on the flag as well: with `acceptPayments` off the page is today's contingency
   * screen whatever the profile says, and no part of the paid state may show through.
   */
  get alreadyPaid(): boolean {
    return this.acceptPayments && this.userProfile.profile?.upfrontFeePaidAt != null;
  }

  /**
   * An admin has marked this user as no longer eligible for the appeal — Abode's evidence
   * review didn't support filing, and the $70 has been refunded in Stripe.
   *
   * This outranks every other state on the page: paid, agreed, lead-present. The reason is
   * the specific dead end it closes — a refund nulls `upfrontFeePaidAt`, which drops the user
   * out of `alreadyPaid` and hands them "Pay $70 and continue to Abode" for a case that was
   * already declined. Checking it last, or alongside the others, would leave that door open.
   *
   * Deliberately **not** gated on `acceptPayments`, unlike `alreadyPaid`. There is no risk of
   * bleeding a payments-era state into today's contingency screen, because this renders only
   * for someone an admin explicitly marked: an unmarked user's page is byte-for-byte what it
   * was, whatever the flag says.
   */
  get partnerIneligible(): boolean {
    return this.userProfile.profile?.partnerIneligibleAt != null;
  }

  /**
   * Whether the server holds an agreement for this user. Distinct from `agreed`, which is only
   * what the two boxes currently say: the boxes are ticked *from* this on load, but a paid user
   * whose agreement was cleared (the admin partner-state reset used to do exactly that) has
   * `alreadyPaid` true and this false. That combination is what `agreeAndContinue` keys off to
   * decide whether it still owes the server a save.
   */
  get hasRecordedAgreement(): boolean {
    return this.userProfile.profile?.partnerAgreedAt != null;
  }

  /**
   * One button drives the whole flow, so its label tracks the flag and the in-flight state.
   * With payments off it is today's label, unchanged.
   */
  get buttonLabel(): string {
    if (this.saving) return 'Saving…';
    if (this.paying) return 'Processing…';
    if (this.alreadyPaid) return 'Continue to Abode';
    return this.acceptPayments ? `Pay $${this.totalFee} and continue to Abode` : 'Agree and continue to Abode';
  }

  /**
   * Restores the checked state for a user who already agreed. Both boxes flip together
   * because the backend stores one combined `partnerAgreedAt`, not one per checkbox.
   * No `loadProfile()` call: `profileRefreshGuard` has already refreshed it.
   *
   * This is also what leaves an already-paid user's boxes checked — in the normal flow the
   * agreement is saved before the charge, so a paid user has a `partnerAgreedAt`. There is
   * deliberately no second mechanism keyed off `upfrontFeePaidAt`: a paid user whose agreement
   * was cleared out from under them gets unchecked, fully interactive boxes and re-consents,
   * rather than being waved through on a record we no longer hold.
   */
  ngOnInit(): void {
    // Ineligible short-circuits the whole bootstrap, before anything else is considered.
    // Nothing below is appropriate for someone whose appeal was declined: no checkout session
    // and so no Stripe.js, and no `session_id` resolution either — that path ends in
    // `openInterstitial()`, which would redirect them to Abode.
    if (this.partnerIneligible) return;
    if (this.userProfile.profile?.partnerAgreedAt) {
      this.agreedTerms = true;
      this.agreedSharing = true;
    }
    if (this.acceptPayments) {
      this.returnedSessionId = this.paymentConfig.returnedSessionId ?? null;
      if (this.returnedSessionId) {
        this.resolveReturnedPayment(this.returnedSessionId);
      } else if (this.isReady && !this.alreadyPaid) {
        // `!alreadyPaid`: someone who has already paid gets no checkout session, no Stripe.js
        // and no payment element. Nothing is left to collect from them, and asking Stripe for
        // a session would be the first step toward charging them twice.
        //
        // The element mounts up front so the user sees what they are paying before agreeing.
        // Driven from `ngOnInit` rather than the template: a getter or an `@if` side effect
        // would open a fresh Checkout Session on every change-detection pass. `startPayment()`
        // is additionally self-guarded, so this is one session per page load either way.
        this.startPayment();
      }
    }
  }

  /**
   * One call, never polled: the endpoint reconciles with Stripe itself, so its answer is
   * authoritative even before the webhook lands.
   *
   * A `paid` session means this user already agreed — the agreement is saved *before* payment —
   * and has now paid, so the only correct move is to finish the flow they started. Nothing here
   * re-saves the agreement or asks for a second checkout session; re-presenting the pay button
   * to someone who just paid is the dead end this fixes, and risks charging them twice.
   */
  private resolveReturnedPayment(sessionId: string): void {
    this.resolvingReturn = true;
    this.payments.getPaymentStatus(sessionId).subscribe({
      next: ({ status }) => {
        this.resolvingReturn = false;
        if (status === 'paid') {
          // The consent that preceded the payment is a fact, whatever the profile happens to
          // say — without this the interstitial's `agreed` guard would strand a paid user.
          this.agreedTerms = true;
          this.agreedSharing = true;
          this.openInterstitial();
        } else if (status === 'pending') {
          this.paymentPending = PAYMENT_PENDING;
        } else {
          this.paymentError = PAYMENT_ERROR;
        }
      },
      // 404 (unknown session, or someone else's) and transport failures are the same case:
      // fall back to the ordinary page so the user can proceed. Never imply they paid.
      error: () => {
        this.resolvingReturn = false;
      },
    });
  }

  get agreed(): boolean {
    return this.agreedTerms && this.agreedSharing;
  }

  /** This user's row from the partner's lead export, matched by email and nested on the profile. */
  get partnerLead(): PartnerLead | null {
    return this.userProfile.profile?.partnerLead ?? null;
  }

  /** A blank `deepLink` counts as not-ready; there is deliberately no generic partner URL fallback. */
  get isReady(): boolean {
    return !!this.partnerLead?.deepLink;
  }

  /**
   * The savings figure as displayed: Abode's own vetted display value, falling back to their raw
   * estimate. Uncapped in both branches — we no longer impose a ceiling of our own.
   *
   * `??` and not `||`: a legitimate `0` from Abode must show as $0, not fall through to a large
   * raw estimate for someone Abode has assessed as saving nothing.
   *
   * Null only when both are null, which the template's `@if (estimatedSavings !== null)` guard
   * needs — the currency pipe renders null as an empty string and would leave the note dangling.
   *
   * Both offer variants read this; the savings card sits above the `acceptPayments` switch.
   */
  get estimatedSavings(): number | null {
    const lead = this.partnerLead;
    return lead?.savingsDisplayCapped ?? lead?.estimatedSavings ?? null;
  }

  /**
   * Free text exactly as the partner exported it — never split, re-cased, or parsed.
   * No `AddressService` fallback on purpose: our record may be a different property.
   */
  get partnerAddress(): string | null {
    return this.partnerLead?.addressSummary ?? null;
  }

  /**
   * Stored verbatim from the partner, so strip a trailing "County" for display only —
   * templates render `{{ countyName }} County`. No title-casing or name mapping: this is
   * legal consent copy. Null when the lead has no county; never falls back to our own.
   */
  get countyName(): string | null {
    const raw = this.partnerLead?.county?.trim();
    if (!raw) return null;
    const stripped = raw.replace(/\s*county\s*$/i, '').trim();
    return stripped || null;
  }

  /** Second-person qualifier for the terms table; see `countyQualifierFirstPerson` for the consent copy. */
  get countyQualifier(): string {
    const county = this.countyName;
    return county ? `the ${county} County` : "your county's";
  }

  /** First-person counterpart of `countyQualifier`, for the consent checkbox's copy. */
  get countyQualifierFirstPerson(): string {
    const county = this.countyName;
    return county ? `the ${county} County` : "my county's";
  }

  /**
   * Saves the agreement before anything else: never redirect without a recorded consent, and
   * never charge without one either. With payments on the confirmation follows the save —
   * a recorded agreement with no payment is recoverable, a payment with no consent is not.
   *
   * An already-paid user is never charged again: no checkout session, no `confirm()`. Same
   * double-charge guard the `session_id` return path applies, reached by a different door (a
   * plain refresh). The rule is "save the agreement if we do not have one, never charge" — not
   * "skip the save". A paid user who already agreed goes straight through, saving nothing; a
   * paid user with no agreement on record (the admin reset used to produce exactly that, and
   * left them on a page with nothing to pay and nothing to click) re-consents here and the
   * save is what makes the click mean something.
   */
  agreeAndContinue(): void {
    if (!this.agreed || this.saving || this.paying) return;
    if (this.alreadyPaid && this.hasRecordedAgreement) {
      this.openInterstitial();
      return;
    }
    this.saving = true;
    this.saveError = '';
    this.userProfile.savePartnerAgreement().subscribe({
      next: () => {
        this.saving = false;
        if (this.acceptPayments && !this.alreadyPaid) {
          void this.payNow();
        } else {
          this.openInterstitial();
        }
      },
      error: () => {
        this.saving = false;
        this.saveError = 'Something went wrong. Please try again.';
      },
    });
  }

  /** Set the instant a session is asked for, so a second request can never be issued. */
  private paymentRequested = false;

  /**
   * Creates the Checkout Session and mounts the Payment Element. Any failure leaves the page
   * with an inline error and no payment UI; the agreement save is first-wins on the server,
   * so a reload costs nothing.
   */
  private startPayment(): void {
    if (this.paymentRequested) return;
    this.paymentRequested = true;
    this.paymentError = '';
    this.preparingPayment = true;
    this.showPayment = true;
    this.payments.createCheckoutSession().subscribe({
      next: ({ clientSecret }) => {
        void this.mountPaymentElement(clientSecret);
      },
      error: () => {
        this.preparingPayment = false;
        this.showPayment = false;
        this.paymentError = PAYMENT_ERROR;
      },
    });
  }

  private async mountPaymentElement(clientSecret: string): Promise<void> {
    try {
      this.checkout = await this.payments.initCheckout(clientSecret);
      // Flush the `@if (showPayment)` block so the mount target exists before mounting into it.
      this.cdr.detectChanges();
      const target = this.paymentElementRef?.nativeElement;
      if (!target) throw new Error('Payment element mount point is missing');
      // `wallets: { link: 'never' }` is what actually removes Link. The session's
      // `payment_method_types: ['card']` does not govern it — Link is a wallet, not a payment
      // method type — and with the wallet left on, the element renders "Save my information"
      // checked by default plus a mandatory mobile number the form never visibly asks for,
      // which is exactly what made `confirm()` fail. Both settings stay; neither is redundant.
      this.checkout.createPaymentElement({ wallets: { link: 'never' } }).mount(target);
      this.preparingPayment = false;
    } catch {
      this.checkout = null;
      this.preparingPayment = false;
      this.showPayment = false;
      this.paymentError = PAYMENT_ERROR;
    }
    this.cdr.detectChanges();
  }

  /**
   * Confirms the payment. Guarded against a second click while one is in flight —
   * double-submitting a payment is materially worse than double-submitting a form —
   * and advances only on an explicit success result.
   */
  async payNow(): Promise<void> {
    if (this.paying || this.preparingPayment || !this.checkout) return;
    this.paying = true;
    this.paymentError = '';
    try {
      const loaded = await this.checkout.loadActions();
      if (loaded.type === 'error') {
        this.failPayment();
        return;
      }
      // `redirect: 'if_required'` keeps a card success on-page so the interstitial still
      // runs here; only methods that genuinely need it leave via the server's return_url.
      const result = await loaded.actions.confirm({ redirect: 'if_required' });
      if (result.type === 'success') {
        this.paying = false;
        this.openInterstitial();
      } else {
        this.failPayment();
      }
    } catch {
      this.failPayment();
    }
    this.cdr.detectChanges();
  }

  /** Payment element stays mounted so the user can correct the card and retry. */
  private failPayment(): void {
    this.paying = false;
    this.paymentError = PAYMENT_ERROR;
  }

  openInterstitial(): void {
    if (!this.agreed) return;
    this.showInterstitial = true;
  }

  goToPartner(): void {
    // Signed, opaque per-lead resume link: passed through byte-for-byte, never rebuilt.
    const deepLink = this.partnerLead?.deepLink;
    if (!deepLink) return;

    // `window.open(url, '_self')` rather than `location.href`: same-tab navigation that is spy-able in tests.
    window.open(deepLink, '_self');
  }
}
